import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Plus, Search, Flame, Link2, Paperclip, Download, ExternalLink, RefreshCw, Trash2, Sparkles,
  Music2, Loader2, AlertCircle, Image as ImageIcon, Play, BookOpen, Send,
} from 'lucide-react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useIsMobile } from '../hooks/useResponsive';
import { Modal, EmptyState, CardGridSkeleton, Avatar } from '../components/ui';
import { can, RUOLI_SOCIAL } from '../lib/ruoli';
import { ordinaClienti } from '../hooks/useClienteAttivo';

/**
 * Trend — bacheca condivisa del team.
 * Incolli un link (Instagram/TikTok/YouTube) o trascini un file: il trend è subito condiviso,
 * il server prova a scaricare il video in background. Poi si aggiunge perché funziona e come usarlo,
 * si vota (🔥) e, quando serve, lo si trasforma in un contenuto del Piano Editoriale.
 */

const STATI = [
  { k: 'nuovo', label: 'Nuovi', one: 'Nuovo', color: 'var(--cyan)' },
  { k: 'da_provare', label: 'Da provare', one: 'Da provare', color: 'var(--yellow)' },
  { k: 'usato', label: 'Usati', one: 'Usato', color: 'var(--green)' },
  { k: 'scaduto', label: 'Scaduti', one: 'Scaduto', color: 'var(--text-dim)' },
];
const TIPI = [
  { k: 'format', label: 'Format' }, { k: 'audio', label: 'Audio' }, { k: 'tema', label: 'Tema' },
  { k: 'estetica', label: 'Estetica' }, { k: 'meme', label: 'Meme' },
];
const PIATTAFORME = {
  instagram: { label: 'Instagram', color: '#e1306c' },
  tiktok: { label: 'TikTok', color: '#25f4ee' },
  youtube: { label: 'YouTube', color: '#ff0000' },
  altro: { label: 'Web', color: 'var(--text-muted)' },
};
const FORMATI = ['Reel', 'Carosello', 'Post Singolo', 'Storia', 'Video YT'];

// Piattaforma da mostrare: i trend senza link nascono da un file caricato a mano
const etichetta = (t) => (t.piattaforma && t.piattaforma !== 'altro') ? PIATTAFORME[t.piattaforma]
  : t.url ? PIATTAFORME.altro : { label: 'File', color: 'var(--text-muted)' };
const statoInfo = (k) => STATI.find(s => s.k === k) || STATI[0];
const isUrl = (s) => /^https?:\/\/\S+$/i.test((s || '').trim());

function fa(ts) {
  const s = (Date.now() - new Date(ts).getTime()) / 1000;
  if (s < 60) return 'adesso';
  if (s < 3600) return `${Math.floor(s / 60)} min fa`;
  if (s < 86400) return `${Math.floor(s / 3600)} h fa`;
  const g = Math.floor(s / 86400);
  return g === 1 ? 'ieri' : g < 7 ? `${g} giorni fa` : new Date(ts).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
}

async function caricaFile(file) {
  const fd = new FormData(); fd.append('file', file);
  const r = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  return { url: r.data.url, nome: file.name };
}

/* ── Anteprima (copertina della card / player nel dettaglio) ─────────── */
function Copertina({ t, player = false }) {
  const m = (t.media || [])[0];
  const p = etichetta(t);
  const base = { position: 'relative', width: '100%', aspectRatio: player ? undefined : '4 / 5', borderRadius: player ? 12 : 10,
    overflow: 'hidden', background: 'var(--bg3)', display: 'flex', alignItems: 'center', justifyContent: 'center' };
  let corpo;
  if (m && m.video && player) {
    corpo = <video src={m.url} poster={m.thumb || undefined} controls playsInline style={{ width: '100%', maxHeight: '70vh', background: '#000', display: 'block' }} />;
  } else if (m && (m.thumb || !m.video)) {
    corpo = <img src={m.thumb || m.url} alt="" loading="lazy" style={{ width: '100%', height: player ? 'auto' : '100%', maxHeight: player ? '70vh' : undefined, objectFit: player ? 'contain' : 'cover', display: 'block' }} />;
  } else {
    // Nessun file (solo link o download in corso): copertina con il colore della piattaforma
    corpo = (
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
        background: `radial-gradient(120% 90% at 50% 0%, color-mix(in srgb, ${p.color} 26%, transparent), transparent 70%), var(--bg3)`,
        color: 'var(--text-muted)', padding: 16, textAlign: 'center' }}>
        {t.download_stato === 'in_corso' ? <Loader2 size={24} className="eden-spin" /> : <Link2 size={24} />}
        <div style={{ fontSize: 12.5, fontWeight: 600 }}>
          {t.download_stato === 'in_corso' ? 'Scarico il video…' : t.url ? `Link ${p.label}` : 'Nessun file'}
        </div>
        {t.download_stato === 'errore' && !player && <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>Apri per allegare il video</div>}
      </div>
    );
  }
  return (
    <div style={{ ...base, minHeight: player ? 260 : undefined }}>
      {corpo}
      {!player && m && m.video && (
        <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <span style={{ width: 38, height: 38, borderRadius: '50%', background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Play size={16} color="#fff" fill="#fff" />
          </span>
        </span>
      )}
      {!player && (
        <span style={{ position: 'absolute', top: 8, left: 8, fontSize: 10.5, fontWeight: 700, padding: '3px 8px', borderRadius: 999,
          background: 'rgba(0,0,0,0.65)', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: p.color }} />{p.label}
        </span>
      )}
      {!player && (t.media || []).length > 1 && (
        <span style={{ position: 'absolute', top: 8, right: 8, fontSize: 10.5, fontWeight: 700, padding: '3px 7px', borderRadius: 999, background: 'rgba(0,0,0,0.65)', color: '#fff' }}>
          1/{t.media.length}
        </span>
      )}
      {!player && t.download_stato === 'in_corso' && m && (
        <span style={{ position: 'absolute', bottom: 8, left: 8, fontSize: 10.5, padding: '3px 8px', borderRadius: 999, background: 'rgba(0,0,0,0.65)', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <Loader2 size={11} className="eden-spin" /> Scarico…
        </span>
      )}
    </div>
  );
}

function VotoBtn({ t, onVoto, grande = false }) {
  return (
    <button type="button" onClick={e => { e.stopPropagation(); onVoto(t); }} aria-pressed={t.votato}
      title={t.votato ? 'Togli il tuo 🔥' : 'Mi piace / lo voglio provare'}
      className="eden-chip" style={{ height: grande ? 34 : 28, padding: '0 10px', gap: 5,
        ...(t.votato ? { background: 'rgba(249,115,22,0.15)', borderColor: 'rgba(249,115,22,0.6)', color: '#f97316' } : {}) }}>
      <Flame size={grande ? 15 : 13} fill={t.votato ? '#f97316' : 'none'} />{Number(t.voti) || 0}
    </button>
  );
}

function TrendCard({ t, onOpen, onVoto }) {
  const st = statoInfo(t.stato);
  return (
    <div className="eden-trend-card" role="button" tabIndex={0} onClick={() => onOpen(t)} onKeyDown={e => { if (e.key === 'Enter') onOpen(t); }}>
      <Copertina t={t} />
      <div style={{ padding: '10px 2px 0', display: 'flex', flexDirection: 'column', gap: 5, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 600 }}>
          <span style={{ color: st.color }}>● {st.one}</span>
          <span style={{ color: 'var(--text-dim)' }}>· {(TIPI.find(x => x.k === t.tipo) || TIPI[0]).label}</span>
          {t.download_stato === 'errore' && <AlertCircle size={12} color="var(--yellow)" title={t.download_errore} style={{ marginLeft: 'auto' }} />}
        </div>
        <div style={{ fontSize: 13.5, fontWeight: 650, color: 'var(--text)', lineHeight: 1.3, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {t.titolo}
        </div>
        {(t.idea || t.perche) && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
            {t.idea || t.perche}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 'auto', paddingTop: 6 }}>
          <Avatar name={t.autore_nome || '?'} size={20} />
          <span style={{ fontSize: 11.5, color: 'var(--text-dim)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {(t.autore_nome || '').trim().split(' ')[0]} · {fa(t.creato_il)}
          </span>
          <VotoBtn t={t} onVoto={onVoto} />
        </div>
      </div>
    </div>
  );
}

/* ── Dettaglio ────────────────────────────────────────────────────────── */
function Campo({ label, children, hint }) {
  return (
    <label style={{ display: 'block', marginBottom: 12 }}>
      <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 5 }}>{label}</span>
      {children}
      {hint && <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-dim)', marginTop: 4 }}>{hint}</span>}
    </label>
  );
}

function Dettaglio({ t, clienti, onClose, onChange, onDelete, puoModificare }) {
  const navigate = useNavigate();
  const { success, error } = useToast();
  const [f, setF] = useState(t);
  const [tagInput, setTagInput] = useState('');
  const [salvo, setSalvo] = useState(false);
  const [mediaIdx, setMediaIdx] = useState(0);
  const [ped, setPed] = useState({ cliente_id: t.cliente_id || '', formato: 'Reel' });
  const [creo, setCreo] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => { setF(t); }, [t]);

  const campi = ['titolo', 'tipo', 'perche', 'idea', 'audio', 'tags', 'cliente_id'];
  const sporco = campi.some(k => JSON.stringify(f[k] ?? null) !== JSON.stringify(t[k] ?? null));
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));

  const salva = async () => {
    setSalvo(true);
    try {
      const body = Object.fromEntries(campi.map(k => [k, f[k] ?? null]));
      const r = await api.patch(`/trend/${t.id}`, body);
      onChange(r.data); success('Trend salvato');
    } catch { /* toast globale */ }
    setSalvo(false);
  };
  const cambiaStato = async (stato) => {
    try { const r = await api.patch(`/trend/${t.id}`, { stato }); onChange(r.data); } catch { /* toast */ }
  };
  const aggiungiTag = () => {
    const v = tagInput.trim().replace(/^#/, '').toLowerCase();
    if (v && !(f.tags || []).includes(v)) set('tags', [...(f.tags || []), v]);
    setTagInput('');
  };
  const allega = async (files) => {
    try {
      const items = [];
      for (const file of files) items.push(await caricaFile(file));
      const r = await api.post(`/trend/${t.id}/media`, items);
      onChange(r.data); success(items.length === 1 ? 'File allegato' : `${items.length} file allegati`);
    } catch { /* toast globale */ }
  };
  const riprova = async () => {
    try { const r = await api.post(`/trend/${t.id}/scarica`); onChange(r.data); } catch { /* toast */ }
  };
  const creaContenuto = async () => {
    if (!ped.cliente_id) { error('Scegli il cliente'); return; }
    setCreo(true);
    try {
      const r = await api.post(`/trend/${t.id}/ped`, ped);
      onChange(r.data.trend);
      success('Contenuto creato nel Piano Editoriale');
      navigate(`/ped/contenuto/${r.data.ped_id}`);
    } catch { /* toast */ }
    setCreo(false);
  };

  const media = t.media || [];
  const m = media[mediaIdx];
  return (
    <Modal open onClose={onClose} title={`${etichetta(t).label} · condiviso ${fa(t.creato_il)}`} width={980}>
      <div className="eden-trend-detail">
        {/* Sinistra: il video */}
        <div style={{ minWidth: 0 }}>
          <Copertina t={{ ...t, media: m ? [m] : [] }} player />
          {media.length > 1 && (
            <div className="eden-scroll-row" style={{ marginTop: 8 }}>
              {media.map((x, i) => (
                <button key={x.url} type="button" onClick={() => setMediaIdx(i)} aria-label={`Elemento ${i + 1}`}
                  style={{ width: 46, height: 58, borderRadius: 8, overflow: 'hidden', padding: 0, cursor: 'pointer', background: 'var(--bg3)',
                    border: `2px solid ${i === mediaIdx ? 'var(--cyan)' : 'transparent'}` }}>
                  {(x.thumb || !x.video) ? <img src={x.thumb || x.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <Play size={14} />}
                </button>
              ))}
            </div>
          )}
          {t.download_stato === 'errore' && (
            <div style={{ display: 'flex', gap: 8, marginTop: 10, padding: '10px 12px', borderRadius: 10, background: 'var(--yellow-dim)', fontSize: 12.5, color: 'var(--text)' }}>
              <AlertCircle size={15} color="var(--yellow)" style={{ flexShrink: 0, marginTop: 1 }} />
              <span>{t.download_errore}</span>
            </div>
          )}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
            {t.url && <a className="eden-btn is-sm" href={t.url} target="_blank" rel="noreferrer"><ExternalLink size={13} />Apri originale</a>}
            {m && <a className="eden-btn is-sm" href={m.url} download={m.nome || true}><Download size={13} />Scarica file</a>}
            <button type="button" className="eden-btn is-sm" onClick={() => fileRef.current?.click()}><Paperclip size={13} />Allega file</button>
            {t.url && t.download_stato === 'errore' && <button type="button" className="eden-btn is-sm" onClick={riprova}><RefreshCw size={13} />Riprova a scaricare</button>}
            <input ref={fileRef} type="file" multiple accept="video/*,image/*" hidden onChange={e => { allega([...e.target.files]); e.target.value = ''; }} />
          </div>
        </div>

        {/* Destra: informazioni */}
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <div className="eden-seg" role="group" aria-label="Stato" style={{ flex: 1, overflowX: 'auto' }}>
              {STATI.map(s => (
                <button key={s.k} type="button" className={t.stato === s.k ? 'is-on' : ''} onClick={() => cambiaStato(s.k)}>{s.one}</button>
              ))}
            </div>
            <VotoBtn t={t} onVoto={() => api.post(`/trend/${t.id}/voto`).then(r => onChange(r.data)).catch(() => {})} grande />
          </div>

          <fieldset disabled={!puoModificare} style={{ border: 'none', padding: 0, margin: 0 }}>
            <Campo label="Titolo">
              <input className="eden-input" value={f.titolo || ''} onChange={e => set('titolo', e.target.value)} />
            </Campo>
            <div className="form-grid-2" style={{ gap: 12 }}>
              <Campo label="Tipo di trend">
                <select className="eden-select" value={f.tipo} onChange={e => set('tipo', e.target.value)}>
                  {TIPI.map(x => <option key={x.k} value={x.k}>{x.label}</option>)}
                </select>
              </Campo>
              <Campo label="Pensato per">
                <select className="eden-select" value={f.cliente_id || ''} onChange={e => set('cliente_id', e.target.value || null)}>
                  <option value="">Tutti i clienti</option>
                  {clienti.map(c => <option key={c.id} value={c.id}>{c.azienda || c.nome}</option>)}
                </select>
              </Campo>
            </div>
            <Campo label="Perché funziona" hint="Cosa aggancia: l'hook, il ritmo, l'audio, il formato…">
              <textarea className="eden-input" rows={2} value={f.perche || ''} onChange={e => set('perche', e.target.value)} />
            </Campo>
            <Campo label="Come lo usiamo" hint="Un'idea concreta per un nostro cliente.">
              <textarea className="eden-input" rows={3} value={f.idea || ''} onChange={e => set('idea', e.target.value)} />
            </Campo>
            <Campo label="Audio">
              <div style={{ position: 'relative' }}>
                <Music2 size={14} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)' }} />
                <input className="eden-input" style={{ paddingLeft: 32 }} placeholder="Nome dell'audio, se conta" value={f.audio || ''} onChange={e => set('audio', e.target.value)} />
              </div>
            </Campo>
            <Campo label="Tag">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                {(f.tags || []).map(tag => (
                  <button key={tag} type="button" className="eden-chip is-on" style={{ height: 28 }} onClick={() => set('tags', f.tags.filter(x => x !== tag))} title="Togli">
                    #{tag} ×
                  </button>
                ))}
                <input className="eden-input" style={{ flex: '1 1 120px', height: 30, fontSize: 12.5 }} placeholder="Aggiungi tag e premi Invio"
                  value={tagInput} onChange={e => setTagInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); aggiungiTag(); } }} onBlur={aggiungiTag} />
              </div>
            </Campo>
          </fieldset>

          {puoModificare && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginBottom: 14 }}>
              <button type="button" className="eden-btn is-sm" onClick={onDelete} style={{ marginRight: 'auto', color: 'var(--red)' }}><Trash2 size={13} />Elimina</button>
              <button type="button" className="eden-btn is-sm is-primary" disabled={!sporco || salvo} onClick={salva}>{salvo ? 'Salvo…' : sporco ? 'Salva modifiche' : 'Salvato'}</button>
            </div>
          )}

          {/* Dal trend al Piano Editoriale */}
          <div style={{ padding: 14, borderRadius: 12, background: 'var(--bg3)', border: '1px solid var(--border)' }}>
            {t.ped_id ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
                <Sparkles size={16} color="var(--green)" />
                <span style={{ flex: 1 }}>Già trasformato in un contenuto{t.cliente_nome ? ` per ${t.cliente_nome}` : ''}.</span>
                <Link className="eden-btn is-sm" to={`/ped/contenuto/${t.ped_id}`}>Apri</Link>
              </div>
            ) : (
              <>
                <div style={{ fontSize: 13, fontWeight: 650, color: 'var(--text)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Send size={14} color="var(--cyan)" />Usalo per un cliente
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <select className="eden-select" style={{ flex: '1 1 150px', height: 34 }} value={ped.cliente_id} onChange={e => setPed(p => ({ ...p, cliente_id: e.target.value }))}>
                    <option value="">Scegli il cliente…</option>
                    {clienti.map(c => <option key={c.id} value={c.id}>{c.azienda || c.nome}</option>)}
                  </select>
                  <select className="eden-select" style={{ flex: '0 1 130px', height: 34 }} value={ped.formato} onChange={e => setPed(p => ({ ...p, formato: e.target.value }))}>
                    {FORMATI.map(x => <option key={x}>{x}</option>)}
                  </select>
                  <button type="button" className="eden-btn is-primary is-sm" style={{ height: 34 }} disabled={creo} onClick={creaContenuto}>
                    <Plus size={14} />{creo ? 'Creo…' : 'Crea contenuto'}
                  </button>
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--text-dim)', marginTop: 6 }}>Crea un contenuto "Idea" nel Piano Editoriale, collegato a questo trend.</div>
              </>
            )}
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--text-dim)', marginTop: 12 }}>
            Condiviso da {t.autore_nome || '—'} · {fa(t.creato_il)}
          </div>
        </div>
      </div>
    </Modal>
  );
}

/* ── Pagina ───────────────────────────────────────────────────────────── */
export default function Trend() {
  const { user } = useAuth();
  const { success } = useToast();
  const isMobile = useIsMobile();
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clienti, setClienti] = useState([]);
  const [link, setLink] = useState('');
  const [invio, setInvio] = useState(false);
  const [drag, setDrag] = useState(false);
  const [aperto, setAperto] = useState(null);
  const [q, setQ] = useState('');
  const [stato, setStato] = useState('');
  const [piatt, setPiatt] = useState('');
  const [tipo, setTipo] = useState('');
  const [cliente, setCliente] = useState('');
  const fileRef = useRef(null);
  const social = can(user, RUOLI_SOCIAL);

  const load = useCallback(() => api.get('/trend', { silent: true }).then(r => { setTrend(r.data || []); setLoading(false); }).catch(() => setLoading(false)), []);
  useEffect(() => { load(); api.get('/clienti').then(r => setClienti(ordinaClienti(r.data || []).filter(c => c.stato === 'attivo'))).catch(() => {}); }, [load]);

  // Finché c'è un download in corso, aggiorna ogni pochi secondi
  const inCorso = trend.some(t => t.download_stato === 'in_corso');
  useEffect(() => {
    if (!inCorso) return undefined;
    const h = setInterval(load, 4000);
    return () => clearInterval(h);
  }, [inCorso, load]);

  const aggiorna = (nuovo) => {
    setTrend(ts => ts.map(t => (t.id === nuovo.id ? nuovo : t)));
    setAperto(a => (a && a.id === nuovo.id ? nuovo : a));
  };

  const condividiLink = async (e) => {
    e && e.preventDefault();
    const u = link.trim();
    if (!isUrl(u)) return;
    setInvio(true);
    try {
      const r = await api.post('/trend', { url: u });
      setTrend(ts => [r.data, ...ts]); setLink('');
      success(r.data.download_stato === 'in_corso' ? 'Trend condiviso: scarico il video…' : 'Trend condiviso');
    } catch { /* toast globale */ }
    setInvio(false);
  };

  const condividiFile = async (files) => {
    if (!files.length) return;
    setInvio(true);
    try {
      const media = [];
      for (const f of files) media.push(await caricaFile(f));
      const titolo = files.length === 1 ? files[0].name.replace(/\.[^.]+$/, '').slice(0, 100) : `${files.length} file`;
      const r = await api.post('/trend', { titolo, media });
      setTrend(ts => [r.data, ...ts]);
      setAperto(r.data);
      success('Trend condiviso: aggiungi perché funziona e come usarlo');
    } catch { /* toast globale */ }
    setInvio(false);
  };

  const elimina = async (t) => {
    if (!window.confirm(`Eliminare "${t.titolo}"? Vengono cancellati anche i video scaricati.`)) return;
    try { await api.delete(`/trend/${t.id}`); setTrend(ts => ts.filter(x => x.id !== t.id)); setAperto(null); success('Trend eliminato'); } catch { /* toast */ }
  };

  const voto = async (t) => {
    try { const r = await api.post(`/trend/${t.id}/voto`); aggiorna(r.data); } catch { /* toast */ }
  };

  const conteggi = useMemo(() => Object.fromEntries(STATI.map(s => [s.k, trend.filter(t => t.stato === s.k).length])), [trend]);
  const visibili = useMemo(() => {
    const qq = q.trim().toLowerCase().replace(/^#/, '');
    return trend.filter(t =>
      (!stato || t.stato === stato) && (!piatt || t.piattaforma === piatt) && (!tipo || t.tipo === tipo) &&
      (!cliente || t.cliente_id === cliente) &&
      (!qq || [t.titolo, t.perche, t.idea, t.audio, ...(t.tags || [])].some(x => (x || '').toLowerCase().includes(qq))));
  }, [trend, q, stato, piatt, tipo, cliente]);

  return (
    <div className="eden-page"
      onDragOver={e => { if ([...e.dataTransfer.types].includes('Files')) { e.preventDefault(); setDrag(true); } }}
      onDragLeave={e => { if (e.currentTarget === e.target) setDrag(false); }}
      onDrop={e => { e.preventDefault(); setDrag(false); condividiFile([...e.dataTransfer.files]); }}>
      {/* Intestazione */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}><Flame size={24} color="#f97316" />Trend</h1>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>Quello che sta funzionando adesso, condiviso dal team.</div>
        </div>
        <Link to="/guida-trend" className="eden-btn"><BookOpen size={15} />{isMobile ? 'Guida' : 'Guida ai trend'}</Link>
      </div>

      {/* Condivisione rapida: incolla un link o trascina un file */}
      <form onSubmit={condividiLink} className="eden-trend-share" style={{ borderColor: drag ? 'var(--cyan)' : undefined, background: drag ? 'var(--cyan-dim)' : undefined }}>
        <Link2 size={17} color="var(--text-dim)" style={{ flexShrink: 0 }} />
        <input value={link} onChange={e => setLink(e.target.value)} onPaste={e => {
          const v = e.clipboardData.getData('text');
          if (isUrl(v)) { e.preventDefault(); setLink(v.trim()); setTimeout(() => document.getElementById('eden-trend-invia')?.click(), 0); }
        }}
          placeholder={drag ? 'Rilascia qui i file…' : (isMobile ? 'Incolla un link…' : 'Incolla un link Instagram, TikTok o YouTube (o trascina qui un video)')}
          aria-label="Link del trend" style={{ flex: 1, minWidth: 0, background: 'none', border: 'none', outline: 'none', color: 'var(--text)', font: 'inherit', fontSize: 14 }} />
        <button type="button" className="eden-btn is-sm is-icon" title="Carica un file" aria-label="Carica un file" onClick={() => fileRef.current?.click()}><Paperclip size={15} /></button>
        <button id="eden-trend-invia" type="submit" className="eden-btn is-sm is-primary" disabled={invio || !isUrl(link)}>
          {invio ? <Loader2 size={14} className="eden-spin" /> : <Plus size={14} />}{!isMobile && 'Condividi'}
        </button>
        <input ref={fileRef} type="file" multiple accept="video/*,image/*" hidden onChange={e => { condividiFile([...e.target.files]); e.target.value = ''; }} />
      </form>

      {/* Filtri */}
      <div className="eden-scroll-row" role="group" aria-label="Stato" style={{ margin: '14px 0 10px' }}>
        <button className={`eden-chip${!stato ? ' is-on' : ''}`} onClick={() => setStato('')}>Tutti <span className="eden-chip-n">{trend.length}</span></button>
        {STATI.map(s => (
          <button key={s.k} className={`eden-chip${stato === s.k ? ' is-on' : ''}`} onClick={() => setStato(stato === s.k ? '' : s.k)}>
            {s.label} <span className="eden-chip-n">{conteggi[s.k]}</span>
          </button>
        ))}
      </div>
      <div className="eden-toolbar">
        <div className="eden-field-search">
          <Search size={15} />
          <input className="eden-input" placeholder="Cerca titolo, idea, audio, #tag…" value={q} onChange={e => setQ(e.target.value)} />
        </div>
        <select className="eden-select" style={{ height: 36, flex: '0 1 150px' }} value={piatt} onChange={e => setPiatt(e.target.value)} aria-label="Piattaforma">
          <option value="">Tutte le piattaforme</option>
          {Object.entries(PIATTAFORME).map(([k, p]) => <option key={k} value={k}>{p.label}</option>)}
        </select>
        <select className="eden-select" style={{ height: 36, flex: '0 1 130px' }} value={tipo} onChange={e => setTipo(e.target.value)} aria-label="Tipo">
          <option value="">Tutti i tipi</option>
          {TIPI.map(x => <option key={x.k} value={x.k}>{x.label}</option>)}
        </select>
        <select className="eden-select" style={{ height: 36, flex: '0 1 170px' }} value={cliente} onChange={e => setCliente(e.target.value)} aria-label="Cliente">
          <option value="">Tutti i clienti</option>
          {clienti.map(c => <option key={c.id} value={c.id}>{c.azienda || c.nome}</option>)}
        </select>
      </div>

      {loading ? <CardGridSkeleton count={8} minWidth={200} height={300} /> : visibili.length === 0 ? (
        <EmptyState icon={trend.length ? Search : ImageIcon}
          title={trend.length ? 'Nessun trend con questi filtri' : 'Ancora nessun trend'}
          hint={trend.length ? 'Prova a togliere qualche filtro.' : 'Hai visto qualcosa che funziona? Incolla il link qui sopra: il team lo vede subito.'} />
      ) : (
        <div className="eden-trend-grid">
          {visibili.map(t => <TrendCard key={t.id} t={t} onOpen={setAperto} onVoto={voto} />)}
        </div>
      )}

      {aperto && (
        <Dettaglio t={aperto} clienti={clienti} onClose={() => setAperto(null)} onChange={aggiorna}
          onDelete={() => elimina(aperto)}
          puoModificare={social || String(aperto.autore_id) === String(user?.id)} />
      )}
    </div>
  );
}
